import { useCallback, useMemo, useState } from 'react';

/**
 * Which of a chart's series its legend has hidden, by key — for a block whose
 * `ChartLegend` takes `onToggle`. A key stays hidden across new data while it
 * is drawn, so a filter change does not bring a hidden brand back. The last
 * series shown cannot be hidden: a chart of nothing says nothing.
 */
export function useLegendToggle(keys: readonly string[]) {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = useCallback(
    (key: string) =>
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else if (keys.some((k) => k !== key && !prev.has(k))) next.add(key);
        return next;
      }),
    [keys],
  );
  const shown = useMemo(
    () => keys.flatMap((key, i) => (hidden.has(key) ? [] : [i])),
    [keys, hidden],
  );
  return {
    /** Whether the series with this key is hidden. */
    isOff: (key: string) => hidden.has(key),
    /** The indexes into `keys` still drawn, in order. */
    shown,
    toggle,
  };
}
